import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { fetchCalendars } from '@shared/utils/connections/calendars'
import type { AttendeeFreeBusy } from '@renderer/components/action-tags/day-timeline-layout'
import { alignFreeBusyAttendees } from './freebusy-align'
import { resolveConnectionId } from './freebusy-connection'
import { zonedWallClockToInstant } from '@renderer/components/action-tags/datetime-wire'
import { compareCodeUnits } from '@renderer/utils/string'

/**
 * Attendee FREE/BUSY for a proposed event window — "Phase 2" of the calendar
 * overlay (extends the Phase-1 day-overlay, #5702). Calls the backend
 * `GET /v1/calendars/freebusy`, whose response shape this parses verbatim:
 *
 *   { success, reconnectRequired, start, end,
 *     attendees: Array<{ email, status: 'busy'|'free'|'unknown', busy: [{start,end}] }> }
 *
 * The connection used is the one that OWNS the event's target calendar: we map
 * the selected `calendarId` (the Calendar edit-tag value, or 'primary') to its
 * `connectionId` via the user's calendar list. Free/busy is queried under that
 * connection's provider, so a Google event uses the Google connection and an
 * Outlook event the Microsoft one.
 *
 * Returns `{ attendees, reconnectRequired }` plus `isLoading`/`error`. Disabled
 * (no fetch) when there are no attendees — the overlay then renders no
 * free/busy section at all.
 */

export interface AttendeeFreeBusyResult {
  attendees: Array<AttendeeFreeBusy>
  /** True when the backend flagged the connection as lacking the broad scope. */
  reconnectRequired: boolean
}

interface UseAttendeeFreeBusyOptions {
  /** Attendee emails to look up (the Guest edit-tag values). */
  emails: Array<string>
  /** The selected calendar id ('primary' or a calendar id), used to resolve the connection. */
  calendarId: string | undefined
  /** Proposed window. A timed draft's start/end are naive WALL-CLOCK Dates in the
   *  event's `timeZone` (see `parseTimedWallClock`); `timeZone` re-anchors them to
   *  true instants for the fetch window. */
  start: Date
  /** Proposed window. */
  end: Date
  /** The event's IANA zone, if it differs from the browser. Used to anchor the
   *  fetch window to the same event-zone day the timeline draws and clamps to. */
  timeZone?: string
  /** The draft action's calendar provider (from its `sourceIntegration`). Scopes
   *  the `'primary'` connection fallback so a Google draft isn't resolved to a
   *  Microsoft account that can't see the guests (and vice versa). */
  provider?: 'google' | 'microsoft'
  /** Gate the whole fetch (e.g. only when the popover is open). */
  enabled?: boolean
}

interface FreeBusyApiResponse {
  success: boolean
  // The backend returns the payload under the standard `{ success, data }`
  // envelope (see GET /v1/calendars/freebusy). Reading these off the top level
  // yields undefined → an always-empty overlay that never prompts reconnect.
  data?: {
    reconnectRequired?: boolean
    attendees?: Array<AttendeeFreeBusy>
  }
}

export function useAttendeeFreeBusy({
  emails,
  calendarId,
  start,
  end,
  timeZone,
  provider,
  enabled = true,
}: UseAttendeeFreeBusyOptions): {
  data: AttendeeFreeBusyResult | undefined
  isLoading: boolean
  error: Error | undefined
} {
  const { getAuthToken } = useAuth()

  // Normalize emails for a stable query key (dedupe + lowercase + sort) so the
  // same set of guests doesn't refetch just because the order changed.
  const normalizedEmails = Array.from(new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))).sort(
    compareCodeUnits,
  )

  // Day-granular window key so dragging the time within a day doesn't refetch
  // (free/busy is queried for the whole day window, below). Keyed on BOTH the
  // local start and end calendar dates so a draft that straddles days (or grows
  // to span more days) refetches the wider window. Keyed on the LOCAL calendar
  // date — the same basis as the fetch window (`setHours(0,0,0,0)`) — not the
  // UTC date, so two local edit days that share a UTC date (possible away from
  // UTC) don't collide on one cache entry.
  const dayKey = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}_${end.getFullYear()}-${end.getMonth()}-${end.getDate()}`

  const hasAttendees = normalizedEmails.length > 0

  const { data, isLoading, error } = useQuery<AttendeeFreeBusyResult>({
    // `timeZone` is part of the key: the same wall-clock day in two zones maps to
    // different absolute fetch windows, so they must not share a cache entry.
    // `provider` is keyed too: it changes which connection 'primary' resolves to.
    queryKey: ['attendee-freebusy', calendarId ?? 'primary', provider ?? '', timeZone ?? '', dayKey, normalizedEmails],
    queryFn: async () => {
      const token = await getAuthToken()

      const calendars = await fetchCalendars(token)
      const connectionId = resolveConnectionId(calendars, calendarId, provider)
      if (!connectionId) {
        // No usable connection (e.g. no connected calendars). Treat every
        // attendee as unknown — never as free.
        return {
          reconnectRequired: false,
          attendees: alignFreeBusyAttendees(emails, []),
        }
      }

      // Query from the start day's midnight through the midnight AFTER the end
      // day, so the window covers every day the draft touches (a draft can
      // straddle days, e.g. 11pm→1am, or span several all-day days). The bands
      // still clamp per-day in the timeline; this just ensures a guest busy on a
      // later day is fetched and counted rather than silently missed.
      //
      // The midnights are the WALL-CLOCK day boundaries; for an event anchored to
      // a non-local zone we re-anchor them to that zone's true instants
      // (`zonedWallClockToInstant`) so the fetched window matches the event-zone
      // day the timeline draws and clamps to (and that the busy reframe uses).
      const wallStart = new Date(start)
      wallStart.setHours(0, 0, 0, 0)
      const wallEnd = new Date(end)
      wallEnd.setHours(0, 0, 0, 0)
      wallEnd.setDate(wallEnd.getDate() + 1)
      const windowStart = zonedWallClockToInstant(wallStart, timeZone)
      const windowEnd = zonedWallClockToInstant(wallEnd, timeZone)

      const params = new URLSearchParams({
        connectionId,
        attendees: emails.join(','),
        start: windowStart.toISOString(),
        end: windowEnd.toISOString(),
      })

      const response = await fetch(getChatApiRoute(`api/v1/calendars/freebusy?${params.toString()}`), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch attendee free/busy (HTTP ${response.status})`)
      }
      const json = (await response.json()) as FreeBusyApiResponse
      // Align to the requested emails so every invited guest appears
      // (busy/free/unknown) even if the provider omitted some — otherwise an
      // incomplete response could shrink `hasAttendees` and hide the footer
      // (including the reconnect prompt) while guests are still on the event.
      return {
        reconnectRequired: !!json.data?.reconnectRequired,
        attendees: alignFreeBusyAttendees(emails, json.data?.attendees ?? []),
      }
    },
    enabled: enabled && hasAttendees,
    staleTime: 60 * 1000,
    retry: 1,
  })

  return { data, isLoading, error: error ?? undefined }
}
